// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import clsx from 'clsx';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    ActionButton,
    ComboBox,
    IComboBoxOption,
    IComboBoxStyles,
    IDropdownOption,
    TextField,
} from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';

import { selectedShippingOfferSelector, isOnSiteRepairStatusSelector } from './../../store/orderCreation';
import ErrorMessage from './../../components/ErrorMessage';
import { SetCostToTwoDecimalPlaces } from './../../helpers/currencyManipulation';
import { setHiddenAnnouncement } from './../../store/hiddenAnnouncement';

import Styles from './shippingMethod.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';
import WalkInCenterDialog from './WalkInCenterDialog';
import { isWalkInCenter } from './../../helpers/shippingChecks';

export interface ItemType {
    key: string;
    listPrice: number;
    text: string;
}
export interface ShippingMethodPropTypes {
    items: ItemType[];
    offerSku: string;
    symptomCode?: string;
    reasonForZ4R?: string;
    reasonForZ4RDescription?: string;
    onSelectionChanged: (key: any) => void;
    readonly?: boolean;
    title: string;
    isOrderSummaryEditing?:boolean;
    onCustomerCommentUpdate: (value: string | undefined ) => void;
}

const dropdownStyles: Partial<IComboBoxStyles> = {
    container: {
        minWidth: 200,
        marginRight: 10,
        maxWidth: 250,
    },
    optionsContainerWrapper: {
        maxHeight: 350,
        width: 200,
    },
    label: {
        fontSize: 12,
        lineHeight: 20,
    },
};

export default function ShippingMethod(props: ShippingMethodPropTypes) {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [selectedOffer, setSelectedOffer] = useState<any>({});
    const [showWalkInCenterDialog, setShowWalkInCenterDialog] = useState<boolean>(false);
    const isOnSiteRepair = useSelector(state => isOnSiteRepairStatusSelector(state));
    const { virtualSku } = selectedOffer;
    const walkInCenterOfferSelected = isWalkInCenter(virtualSku);

    const options: IDropdownOption[] = props.items.map((option: any): IComboBoxOption => ({
        key: option.key,
        text: option.text,
    }));

    const selectedShippingOffers = useSelector(state => selectedShippingOfferSelector(state));
    
    function onClickShowWalkInSearchDialog() {
        setShowWalkInCenterDialog(true);
    }

    function onWalkInCenterDialogClose() {
        setShowWalkInCenterDialog(false);
    }

    function onChangeMethod(
        event: any,
        selectedOption?: IDropdownOption
    ) {
        if (selectedOption) {
            logPageAction('Select Shipping Method for combo item', { itemSelected: selectedOption.text });
            select(selectedOption);
        }
    }

    function select(selectedOption: IDropdownOption) {
        const selectedItem = props.items.find(option => option.key === selectedOption.key) || { listPrice: 0, currency: '' };
        setSelectedOffer(selectedItem);
        props.onSelectionChanged({ itemOfferSku: props.offerSku, ...selectedItem });
    }

    function onCustomerCommentUpdate(event: any, value: string | undefined) {
        props.onCustomerCommentUpdate(value);
    }

    const label = props.title;
    const description = selectedOffer.description ? selectedOffer.description : '---';

    // if there is only one option, select it
    useEffect(() => {
        if (options && options.length === 1) {
            select(options[0]);
        }
        
        if(Object.keys(selectedShippingOffers).length !== 0 && props.readonly && !props.isOrderSummaryEditing) {
            setSelectedOffer(selectedShippingOffers[Object.keys(selectedShippingOffers)[0]]);
        }
    }, []);

    if (!options || options.length === 0) {
        const message = props.title ? t('shipping_offer_not_configured', { componentName: props.title }) : '';
        dispatch(setHiddenAnnouncement({
            message,
        }));
        return (
            <ErrorMessage className={Styles.shippingMethod} message={message} traceData={{ options, selectedOffer }} />
        );
    }

    function WalkInCenter(props: any): JSX.Element {

        if (walkInCenterOfferSelected) {
            return (<div>
                <WalkInCenterDialog
                    hide={!showWalkInCenterDialog}
                    onClose={onWalkInCenterDialogClose} />
                <ActionButton
                    className={Styles.actionButton}
                    onClick={onClickShowWalkInSearchDialog}
                    iconProps={{
                        ariaLabel: t('find_locations'),
                        iconName: 'POI',
                    }} >
                    {t('find_locations')}
                </ActionButton>
            </div>);
        }
        return <></>;
    }

    return (
        <div className={Styles.borderDown}>
            <div className={clsx(Styles.shippingMethod, 'ms-Grid-row', Styles.columnAlignment)}>
                <div className={clsx('ms-Grid-col', 'ms-sm5')} >
                    {!props.readonly && <ComboBox
                        id="shippingMethod"
                        ariaLabel={label}
                        autoComplete="on"
                        label={label}
                        required={true}
                        selectedKey={selectedOffer.key}
                        placeholder={t('select_shipping_method')}
                        onChange={onChangeMethod}
                        openOnKeyboardFocus={true}
                        options={options}
                        styles={dropdownStyles}
                        useComboBoxAsMenuWidth={true}

                    />}
                    {props.readonly && <div>
                        <span className={Styles.label}>{label}</span>
                        <p className={Styles.label}>{selectedOffer.text}</p>
                    </div>}
                    <WalkInCenter />
                    {props.reasonForZ4R && (
                        <div className={Styles.returnInfoBlock}>
                            <span className={Styles.returnInfoLabel}>{t('z4r_reason')}:</span>
                            <p className={Styles.returnInfoValue}>{props.reasonForZ4R}</p>
                        </div>
                    )}
                    {props.reasonForZ4RDescription && (
                        <div className={Styles.returnInfoBlock}>
                            <span className={Styles.returnInfoLabel}>{t('z4r_reason_description')}:</span>
                            <p className={Styles.returnInfoValue}>{props.reasonForZ4RDescription}</p>
                        </div>
                    )}
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm6', Styles.columnAlignment)} >
                    <span className={Styles.additionalInfo}> {description}</span>
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm1', Styles.columnAlignment)}>
                    <p className={Styles.additionalInfo}> {`${SetCostToTwoDecimalPlaces(selectedOffer.listPrice, selectedOffer.currency)}`}</p>
                </div>
            </div>
            {
                isOnSiteRepair ?
                    <div className={clsx(Styles.shippingMethod, 'ms-Grid-row', Styles.columnAlignment)}>
                        <div className={clsx('ms-Grid-col', 'ms-sm5')} >
                            <TextField
                                name="problem_desc"
                                label={t('customer_comments')}
                                multiline={true}
                                rows={5}
                                placeholder={t('customer_comments_placeholder')}
                                onChange={onCustomerCommentUpdate}
                            />
                        </div>
                    </div> : ''
            }
        </div>

    );
}
